import jsPDF from 'jspdf';
import {
  BGW_CLUSTERS,
  getMeasureDef,
  type BgwMeasureState,
} from './bgwCatalog';

export interface GenerateBgwPdfParams {
  salonName: string;
  salonOwner: string;
  street: string;
  zip: string;
  city: string;
  teamSize: number;
  assessedAt: string;
  confirmerName: string;
  measures: BgwMeasureState[];
  firstAiderName?: string;
  fireSafetyOfficerName?: string;
}

function formatDateForFilename(isoOrGerman: string): string {
  const trimmed = String(isoOrGerman || '').trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    return trimmed.slice(0, 10);
  }
  const m = trimmed.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (m) {
    const dd = m[1].padStart(2, '0');
    const mm = m[2].padStart(2, '0');
    return `${m[3]}-${mm}-${dd}`;
  }
  return new Date().toISOString().slice(0, 10);
}

export interface BuildBgwPdfResult {
  doc: jsPDF;
  blob: Blob;
  filename: string;
}

/** Lesbare Druckgrößen (pt) — lieber 2 Seiten als 7-pt-Mikroschrift. */
const FONT = {
  title: 15,
  subtitle: 11,
  header: 11,
  meta: 10,
  legal: 10,
  cluster: 11,
  tableHead: 9.5,
  body: 10,
  disclaimer: 9.5,
  signature: 10,
} as const;

export function buildBgwAssessmentPdf(params: GenerateBgwPdfParams): BuildBgwPdfResult {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = 210;
  const pageHeight = 297;
  const margin = 16;
  const contentWidth = pageWidth - margin * 2;
  let currentY = 18;

  const checkPageBreak = (neededHeight: number) => {
    if (currentY + neededHeight > pageHeight - margin - 14) {
      doc.addPage();
      currentY = 18;
    }
  };

  const addressLine = [params.street, [params.zip, params.city].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');

  // --- Header (rechts) ---
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(FONT.header);
  doc.setTextColor(30, 41, 59);
  doc.text(params.salonName || 'Salon', pageWidth - margin, currentY, { align: 'right' });
  currentY += 5.5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(FONT.meta);
  doc.setTextColor(71, 85, 105);
  doc.text(`Inhaber: ${params.salonOwner || '—'}`, pageWidth - margin, currentY, {
    align: 'right',
  });
  currentY += 4.5;
  if (addressLine) {
    doc.text(addressLine, pageWidth - margin, currentY, { align: 'right' });
    currentY += 4.5;
  }
  doc.text(`Teamgröße: ${params.teamSize}`, pageWidth - margin, currentY, {
    align: 'right',
  });
  currentY += 10;

  // --- Titel ---
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(FONT.title);
  doc.setTextColor(15, 23, 42);
  doc.text('GEFÄHRDUNGSBEURTEILUNG', margin, currentY);
  currentY += 6.5;

  doc.setFontSize(FONT.subtitle);
  doc.setTextColor(180, 83, 9);
  doc.text('Friseurhandwerk (BGW-Kernbereiche)', margin, currentY);
  currentY += 7;

  doc.setDrawColor(203, 213, 225);
  doc.setLineWidth(0.5);
  doc.line(margin, currentY, pageWidth - margin, currentY);
  currentY += 6;

  // --- Rechtsverweis ---
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(FONT.legal);
  doc.setTextColor(51, 65, 85);
  const legalTitle =
    'Dokumentation der Gefährdungsbeurteilung gem. § 5 ArbSchG i.V.m. DGUV Vorschrift 2 und TRGS 530';
  const legalLines = doc.splitTextToSize(legalTitle, contentWidth);
  doc.text(legalLines, margin, currentY);
  currentY += legalLines.length * 4.6 + 5;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(FONT.meta);
  doc.text(`Prüfdatum: `, margin, currentY);
  doc.setFont('helvetica', 'normal');
  doc.text(params.assessedAt || '—', margin + 26, currentY);

  doc.setFont('helvetica', 'bold');
  doc.text(`Bestätigt durch: `, margin + 78, currentY);
  doc.setFont('helvetica', 'normal');
  const confirmer = doc.splitTextToSize(params.confirmerName || '—', contentWidth - 118);
  doc.text(confirmer, margin + 112, currentY);
  currentY += Math.max(6, confirmer.length * 4.5) + 4;

  // --- Cluster-Tabellen ---
  const colMeasure = contentWidth * 0.54;
  const colStatus = contentWidth * 0.24;
  const colNote = contentWidth * 0.22;
  const rowLineH = 4.6;

  BGW_CLUSTERS.forEach((cluster) => {
    checkPageBreak(22);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(FONT.cluster);
    doc.setTextColor(15, 23, 42);
    doc.text(cluster.title, margin, currentY);
    currentY += 6;

    // Table header
    doc.setFillColor(241, 245, 249);
    doc.rect(margin, currentY - 4, contentWidth, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(FONT.tableHead);
    doc.setTextColor(71, 85, 105);
    doc.text('Maßnahme', margin + 1.5, currentY);
    doc.text('Status', margin + colMeasure + 1.5, currentY);
    doc.text('Bemerkung', margin + colMeasure + colStatus + 1.5, currentY);
    currentY += 6;

    cluster.measureIds.forEach((measureId) => {
      const def = getMeasureDef(measureId);
      const state = params.measures.find((m) => m.id === measureId);
      if (!def || !state) return;

      const statusText = state.compliant
        ? 'Umgesetzt / Eingehalten'
        : 'Abweichung / Geplante Maßnahme';

      let noteText = state.note || '';
      if (def.nameFieldKey === 'firstAiderName') {
        const namePart = `Name: ${params.firstAiderName?.trim() || 'Nicht benannt'}`;
        noteText = [noteText, namePart].filter(Boolean).join(' · ');
      }
      if (def.nameFieldKey === 'fireSafetyOfficerName') {
        const namePart = `Name: ${params.fireSafetyOfficerName?.trim() || 'Nicht benannt'}`;
        noteText = [noteText, namePart].filter(Boolean).join(' · ');
      }
      if (!noteText) noteText = '—';

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(FONT.body);
      const measureLines = doc.splitTextToSize(def.title, colMeasure - 3);
      const statusLines = doc.splitTextToSize(statusText, colStatus - 3);
      const noteLines = doc.splitTextToSize(noteText, colNote - 3);
      const rowH =
        Math.max(measureLines.length, statusLines.length, noteLines.length) * rowLineH + 2.5;

      checkPageBreak(rowH + 2);

      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.25);
      doc.line(margin, currentY - 2.8, pageWidth - margin, currentY - 2.8);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(FONT.body);
      doc.setTextColor(30, 41, 59);
      doc.text(measureLines, margin + 1.5, currentY);

      if (state.compliant) {
        doc.setTextColor(21, 128, 61);
      } else {
        doc.setTextColor(180, 83, 9);
      }
      doc.text(statusLines, margin + colMeasure + 1.5, currentY);

      doc.setTextColor(71, 85, 105);
      doc.text(noteLines, margin + colMeasure + colStatus + 1.5, currentY);

      currentY += rowH;
    });

    currentY += 5;
  });

  // --- Abschluss / Disclaimer ---
  checkPageBreak(48);
  currentY += 2;
  doc.setDrawColor(203, 213, 225);
  doc.setLineWidth(0.4);
  doc.line(margin, currentY, pageWidth - margin, currentY);
  currentY += 7;

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(FONT.disclaimer);
  doc.setTextColor(100, 116, 139);
  const disclaimer =
    'Hinweis: Dieses Dokument dient als Dokumentationshilfe für den Saloninhaber gemäß § 5 ArbSchG. Es ersetzt keine behördliche Vor-Ort-Begehung und keine individuelle Gefährdungsbeurteilung durch eine Fachkraft für Arbeitssicherheit. Der/die Unterzeichnende bestätigt, dass die angegebenen Maßnahmen dem tatsächlichen Stand im Betrieb entsprechen.';
  const disclaimerLines = doc.splitTextToSize(disclaimer, contentWidth);
  doc.text(disclaimerLines, margin, currentY);
  currentY += disclaimerLines.length * 4.4 + 12;

  // --- Signatur ---
  checkPageBreak(30);
  const sigWidth = contentWidth * 0.55;
  doc.setDrawColor(148, 163, 184);
  doc.setLineWidth(0.35);
  doc.line(margin, currentY + 14, margin + sigWidth, currentY + 14);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(FONT.signature);
  doc.setTextColor(100, 116, 139);
  const ortDatum = [params.city, params.assessedAt].filter(Boolean).join(', ') || 'Ort, Datum';
  doc.text(ortDatum, margin, currentY + 19);
  doc.text('Unterschrift Saloninhaber/in', margin, currentY + 24);

  const fileDate = formatDateForFilename(params.assessedAt);
  const cleanSalon = (params.salonName || params.salonOwner || 'Salon')
    .replace(/[^a-zA-Z0-9äöüÄÖÜß_-]/g, '_')
    .replace(/_+/g, '_');
  const filename = `BGW_Gefaehrdungsbeurteilung_${cleanSalon}_${fileDate}.pdf`;

  doc.setProperties({
    title: filename.replace('.pdf', ''),
    subject: 'BGW-Gefährdungsbeurteilung Friseurhandwerk gem. § 5 ArbSchG',
    author: params.salonOwner || params.salonName || 'PreisExpert',
  });

  const rawBlob = doc.output('blob');
  const blob = new Blob([rawBlob], { type: 'application/pdf' });
  return { doc, blob, filename };
}
